import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { useSelector } from "react-redux";

import messages from "../../../../../assets/locale/messages";
import {
  UIActionModal,
  UIChangeSummary,
  UIRadioGroup,
  UISelect,
} from "../../../../ui";

const approveModes = {
  current: "current",
  change: "change",
};

const ApproveRequestModal = ({
  open,
  variant,
  applicants,
  roleOptions,
  shiftOptions,
  onClose,
  onApprove,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const t =
    messages[lang].events.viewEvents.applicantsTab.joiningRequests
      .approveModal;
  const [mode, setMode] = useState(approveModes.current);
  const [role, setRole] = useState(null);
  const [shift, setShift] = useState(null);
  const isEditRole = variant === "editRole";
  const isChangeMode = isEditRole || mode === approveModes.change;
  const isBulk = applicants.length > 1;
  const primaryApplicant = applicants[0];

  useEffect(() => {
    if (!open) return;
    setMode(isEditRole ? approveModes.change : approveModes.current);
    setRole(null);
    setShift(null);
  }, [isEditRole, open]);

  const options = useMemo(
    () => [
      { value: approveModes.current, label: t.currentRole },
      { value: approveModes.change, label: t.changeRole },
    ],
    [t],
  );
  const availableShiftOptions = useMemo(
    () =>
      role?.id
        ? shiftOptions.filter((item) => String(item.roleId) === String(role.id))
        : shiftOptions,
    [role, shiftOptions],
  );

  const isApproveDisabled = isChangeMode && (!role?.id || !shift?.id);
  const message = isEditRole
    ? t.editRoleBulkMessage.replace("{count}", applicants.length)
    : isBulk
      ? t.bulkMessage.replace("{count}", applicants.length)
      : t.singleMessage;
  const hasRoleShiftChange =
    isChangeMode && role?.id && shift?.id;
  const shiftChangeLabel = t.shiftTemplate.replace("{shift}", shift?.name || "");
  const roleShiftChangeLabel = [role?.name, shiftChangeLabel]
    .filter(Boolean)
    .join(" ");
  const changeSummaryItems = isBulk
    ? [
        t.bulkRoleChangeTo.replace("{role}", role?.name || ""),
        t.bulkShiftChangeTo.replace("{shift}", shift?.name || ""),
      ]
    : [
        primaryApplicant?.requestedRole,
        roleShiftChangeLabel,
      ].filter(Boolean);

  return (
    <UIActionModal
      open={open}
      onClose={onClose}
      title={isEditRole ? t.editRoleTitle : t.title}
      maxWidth="sm"
      PaperProps={{ className: "event-applicants-approve-modal" }}
      actions={
        <UIActionModal.Actions
          cancelLabel={t.cancel}
          confirmLabel={isEditRole ? t.saveRole : t.approve}
          confirmDisabled={isApproveDisabled}
          onCancel={onClose}
          onConfirm={() =>
            onApprove({
              mode: isChangeMode ? approveModes.change : mode,
              role,
              shift,
            })
          }
        />
      }
    >
      {!isBulk && primaryApplicant ? (
        <UIActionModal.UserInfo
          name={primaryApplicant.name}
          image={primaryApplicant.image}
          description={`${t.requestedRole}: ${primaryApplicant.requestedRole}`}
        />
      ) : (
        <Typography className="event-applicants-approve-modal__bulk">
          {message}
        </Typography>
      )}
      <UIActionModal.Divider />
      <Typography className="ui-action-modal__body-text">
        {isEditRole ? t.editRoleHint : isBulk ? t.bulkHint : t.singleHint}
      </Typography>
      {!isEditRole && (
        <UIRadioGroup
          name="approve-mode"
          value={mode}
          options={options}
          onChange={setMode}
        />
      )}
      {isChangeMode && (
        <div className="event-applicants-approve-modal__change-fields">
          <UISelect
            label={t.newRole}
            placeholder={t.selectRole}
            value={role}
            options={roleOptions}
            onChange={(value) => {
              setRole(value);
              setShift(null);
            }}
          />
          <UISelect
            label={t.newShift}
            placeholder={t.selectShift}
            value={shift}
            options={availableShiftOptions}
            onChange={setShift}
          />
          {hasRoleShiftChange && (
            <UIChangeSummary
              items={changeSummaryItems}
              separator={isBulk ? "none" : "chevron"}
              description={t.changeSummaryDescription}
            />
          )}
        </div>
      )}
    </UIActionModal>
  );
};

ApproveRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  variant: PropTypes.oneOf(["approve", "editRole"]),
  applicants: PropTypes.arrayOf(PropTypes.object),
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  shiftOptions: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
};

ApproveRequestModal.defaultProps = {
  variant: "approve",
  applicants: [],
  roleOptions: [],
  shiftOptions: [],
};

export default ApproveRequestModal;
